import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Filter, RefreshCcw, Search, ChevronDown, ChevronUp,
} from 'lucide-react';
import { callAdminGetAuditLogs } from '../../services/admin';

type AuditLog = {
  id: string;
  category?: string;
  eventType?: string;
  outcome?: string;
  actor?: Record<string, unknown> | null;
  target?: Record<string, unknown> | null;
  source?: Record<string, unknown> | null;
  details?: Record<string, unknown> | null;
  reason?: string | null;
  paymentStatus?: string | null;
  pfPaymentId?: string | null;
  createdAt?: any;
  receivedAt?: any;
  updatedAt?: any;
  rawPayload?: Record<string, unknown> | null;
};

type Filters = {
  uid: string;
  productId: string;
  eventType: string;
  outcome: string;
  limit: number;
};

const defaultFilters: Filters = {
  uid: '',
  productId: 'all',
  eventType: '',
  outcome: 'all',
  limit: 100,
};

const outcomePillClass = (outcome: string) => {
  const v = (outcome || '').toLowerCase();
  if (v === 'success' || v === 'processed') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (v === 'error' || v === 'rejected') return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  if (v === 'received') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
  return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
};

const formatTs = (ts: any) => {
  if (!ts) return null;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?._seconds === 'number') return ts._seconds * 1000;
  const parsed = new Date(ts).getTime();
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const formatDate = (primary: any, fallback?: any) => {
  const ms = formatTs(primary) || formatTs(fallback);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNested = (obj: Record<string, unknown> | null | undefined, key: string): string => {
  const value = obj?.[key];
  return value == null ? '' : String(value);
};

const summaryForLog = (log: AuditLog) => {
  const reason = String(log.reason || getNested(log.details || null, 'reason') || '').trim();
  if (reason) return reason.replace(/_/g, ' ');
  const paymentId = String(log.pfPaymentId || getNested(log.target || null, 'pfPaymentId') || '').trim();
  if (paymentId) return `pfPaymentId: ${paymentId}`;
  const status = String(log.paymentStatus || getNested(log.details || null, 'paymentStatus') || '').trim();
  if (status) return `status: ${status}`;
  const amount = String(getNested(log.details || null, 'amountGross') || getNested(log.details || null, 'amount') || '').trim();
  if (amount) return `amount: ${amount}`;
  return '—';
};

const AdminAuditLogs: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [search, setSearch] = useState('');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadLogs = async (activeFilters: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const payload: {
        limit: number;
        uid?: string;
        eventType?: string;
        outcome?: string;
        productId?: string;
      } = { limit: activeFilters.limit };

      const uid = activeFilters.uid.trim();
      const eventType = activeFilters.eventType.trim();

      if (uid) payload.uid = uid;
      if (eventType) payload.eventType = eventType;
      if (activeFilters.outcome !== 'all') payload.outcome = activeFilters.outcome;
      if (activeFilters.productId !== 'all') payload.productId = activeFilters.productId;

      const result = await callAdminGetAuditLogs(payload);
      setLogs(result.logs || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await loadLogs(defaultFilters);
      } finally {
        if (!mounted) return;
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filteredLogs = useMemo(() => {
    if (!search.trim()) return logs;
    const q = search.trim().toLowerCase();
    return logs.filter((log) => {
      const user = getNested(log.target || null, 'uid') || getNested(log.actor || null, 'uid');
      const product = getNested(log.target || null, 'productId');
      const textBlob = [
        log.id,
        log.category,
        log.eventType,
        log.outcome,
        log.reason,
        log.paymentStatus,
        log.pfPaymentId,
        user,
        product,
        summaryForLog(log),
      ].filter(Boolean).join(' ').toLowerCase();
      return textBlob.includes(q);
    });
  }, [logs, search]);

  const applyFilters = async () => {
    await loadLogs(filters);
  };

  const resetFilters = async () => {
    setFilters(defaultFilters);
    setSearch('');
    await loadLogs(defaultFilters);
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <ClipboardList className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Audit Logs</h1>
              <p className="text-slate-600 dark:text-gray-400">{filteredLogs.length} logs shown</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="glass-effect rounded-2xl p-4 md:p-5 shadow-glass mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
            <input
              type="text"
              value={filters.uid}
              onChange={(e) => setFilters((f) => ({ ...f, uid: e.target.value }))}
              placeholder="Filter UID"
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="text"
              value={filters.eventType}
              onChange={(e) => setFilters((f) => ({ ...f, eventType: e.target.value }))}
              placeholder="Filter event type"
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={filters.outcome}
              onChange={(e) => setFilters((f) => ({ ...f, outcome: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All outcomes</option>
              <option value="success">success</option>
              <option value="processed">processed</option>
              <option value="received">received</option>
              <option value="rejected">rejected</option>
              <option value="error">error</option>
              <option value="info">info</option>
            </select>
            <select
              value={filters.productId}
              onChange={(e) => setFilters((f) => ({ ...f, productId: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All products</option>
              <option value="vetaxis">vetaxis</option>
              <option value="staffaxis">staffaxis</option>
              <option value="noteaxis">noteaxis</option>
              <option value="suite">suite</option>
              <option value="custom">custom</option>
            </select>
            <select
              value={filters.limit}
              onChange={(e) => setFilters((f) => ({ ...f, limit: Number(e.target.value) }))}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
            <div className="flex items-center gap-2">
              <button
                onClick={applyFilters}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600"
              >
                <Filter className="w-4 h-4" /> Apply
              </button>
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-gray-700 text-sm text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search loaded logs"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={() => loadLogs(filters)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-gray-700 text-sm text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800"
            >
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </motion.div>

        {error && (
          <motion.div
            className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error}
          </motion.div>
        )}

        <motion.div
          className="glass-effect rounded-2xl shadow-glass overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {loading ? (
            <div className="p-10 text-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-600 dark:text-gray-400">Loading audit logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-10 text-center text-slate-600 dark:text-gray-400">
              No audit logs match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Outcome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Summary</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {filteredLogs.map((log) => {
                    const user = getNested(log.target || null, 'uid') || getNested(log.actor || null, 'uid') || '—';
                    const product = getNested(log.target || null, 'productId') || '—';
                    const isExpanded = expandedId === log.id;
                    return (
                      <React.Fragment key={log.id}>
                        <tr className="hover:bg-slate-50/50 dark:hover:bg-gray-800/40">
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                            {formatDate(log.createdAt, log.receivedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                            <span className="font-medium">{log.eventType || '—'}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${outcomePillClass(log.outcome || '')}`}>
                              {log.outcome || 'info'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">{user}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap capitalize">{product}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-gray-400">{summaryForLog(log)}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : log.id)}
                              className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="px-4 pb-4">
                              <pre className="text-xs bg-slate-100 dark:bg-gray-900/70 text-slate-800 dark:text-gray-200 rounded-lg p-3 overflow-auto max-h-80">
                                {JSON.stringify(log, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAuditLogs;
